import { supabase } from './supabase';

/**
 * Upload a signature blob to the `signatures` bucket. Path convention is
 * `<org_id>/<sign_in_id>/<side>.png` so storage RLS policies work.
 */
export async function uploadSignature(opts: {
  orgId: string;
  signInId: string;
  side: 'in' | 'out';
  blob: Blob;
}): Promise<string> {
  const path = `${opts.orgId}/${opts.signInId}/${opts.side}.png`;
  const { error } = await supabase.storage
    .from('signatures')
    .upload(path, opts.blob, { contentType: 'image/png', upsert: true });
  if (error) throw error;
  return path;
}

/**
 * Generate a short-lived signed URL for a private storage object.
 * Use this when you want to display an uploaded image (signatures, RAMS,
 * accident photos) in the UI.
 */
export async function getSignedUrl(
  bucket: string,
  path: string,
  expiresIn = 3600
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn);
  if (error) throw error;
  return data.signedUrl;
}

/**
 * Upload an organisation logo. Path: `<org_id>/logo.<ext>` so the storage RLS
 * policy on the `org-logos` bucket allows it.
 */
export async function uploadLogo(orgId: string, file: File): Promise<string> {
  const extFromName = file.name.split('.').pop()?.toLowerCase();
  const ext = (extFromName && /^[a-z0-9]+$/.test(extFromName)) ? extFromName : 'png';
  const path = `${orgId}/logo.${ext}`;
  const { error } = await supabase.storage.from('org-logos').upload(path, file, {
    contentType: file.type || 'image/png',
    upsert: true,
  });
  if (error) throw error;
  return path;
}

export async function removeLogo(orgId: string, path: string): Promise<void> {
  // Path is the full storage path; pass as-is to remove()
  const { error } = await supabase.storage.from('org-logos').remove([path]);
  if (error) throw error;
  // Also clear the org row's logo_path
  const { error: e2 } = await supabase
    .from('organisations')
    .update({ logo_path: null })
    .eq('id', orgId);
  if (e2) throw e2;
}

/**
 * Upload a RAMS file (PDF or image) into the `rams-files` bucket.
 * Path: `<org_id>/<rams_id>.<ext>` so storage RLS allows it.
 */
export async function uploadRams(orgId: string, ramsId: string, file: File): Promise<string> {
  const extFromName = file.name.split('.').pop()?.toLowerCase();
  const ext = extFromName && /^[a-z0-9]+$/.test(extFromName) ? extFromName : 'pdf';
  const path = `${orgId}/${ramsId}.${ext}`;
  const { error } = await supabase.storage.from('rams-files').upload(path, file, {
    contentType: file.type || 'application/pdf',
    upsert: true,
  });
  if (error) throw error;
  return path;
}

/**
 * Upload a document file into the `documents` bucket.
 * Path: `<org_id>/<doc_id>.<ext>`
 */
export async function uploadDocument(orgId: string, docId: string, file: File): Promise<string> {
  const extFromName = file.name.split('.').pop()?.toLowerCase();
  const ext = extFromName && /^[a-z0-9]+$/.test(extFromName) ? extFromName : 'pdf';
  const path = `${orgId}/${docId}.${ext}`;
  const { error } = await supabase.storage.from('documents').upload(path, file, {
    contentType: file.type || 'application/pdf',
    upsert: true,
  });
  if (error) throw error;
  return path;
}

/**
 * Upload a permit file (issued or closed PDF) into the `permits` bucket.
 * Path: `<org_id>/<permit_id>.<ext>`
 */
export async function uploadPermit(orgId: string, permitId: string, file: File): Promise<string> {
  const extFromName = file.name.split('.').pop()?.toLowerCase();
  const ext = extFromName && /^[a-z0-9]+$/.test(extFromName) ? extFromName : 'pdf';
  const path = `${orgId}/${permitId}.${ext}`;
  const { error } = await supabase.storage.from('permits').upload(path, file, {
    contentType: file.type || 'application/pdf',
    upsert: true,
  });
  if (error) throw error;
  return path;
}

/**
 * Generate a signed URL for a document or permit suitable for sharing.
 * Default expiry 7 days; pass expirySeconds to override.
 */
export async function getShareUrl(
  bucket: 'documents' | 'permits' | 'signed-pdfs' | 'rams-files',
  path: string,
  expirySeconds: number = 7 * 24 * 60 * 60,
): Promise<string> {
  return getSignedUrl(bucket, path, expirySeconds);
}
